import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { API, auth, err, blankForm, formFromInvoice, payload, dueFor, copyPayLink } from './invoiceApi';
import { CustomerSection } from './CustomerSection';
import { LineItems } from './LineItems';
import { TaxTotalsPanel } from './TaxTotalsPanel';
import { ServicesEditor } from './ServicesEditor';

const useCalc = (form, token) => {
  const [calc, setCalc] = useState(null);
  const [error, setError] = useState('');
  const key = JSON.stringify(payload(form));
  useEffect(() => {
    if (!form.customer_name.trim() || !form.items.length || form.items.some((l) => !l.name.trim() || !(Number(l.quantity) > 0))) {
      setError('Add a customer name and give every line a name and quantity to calculate'); return undefined;
    }
    const t = setTimeout(() => axios.post(`${API}/admin/invoices/calculate`, payload(form), auth(token))
      .then((r) => { setCalc(r.data); setError(''); }).catch((e) => setError(err(e, 'Could not calculate'))), 400);
    return () => clearTimeout(t);
  }, [key, token]); // eslint-disable-line react-hooks/exhaustive-deps
  return { calc, error };
};

const Footer = ({ form, set, terms }) => (
  <section className="grid md:grid-cols-2 gap-4" data-testid="inv-footer-fields">
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Payment terms</Label>
          <select value={form.payment_terms} onChange={(e) => set({ payment_terms: e.target.value, due_date: dueFor(e.target.value) || form.due_date })} className="w-full mt-1 px-3 py-2 border rounded-md text-sm" data-testid="inv-terms">
            {Object.entries(terms).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><Label className="text-xs">Due date</Label><Input type="date" value={form.due_date || ''} onChange={(e) => set({ due_date: e.target.value })} className="mt-1" data-testid="inv-due-date" /></div>
      </div>
      <label className={`flex items-start gap-2 text-sm p-3 rounded-md border ${form.is_test ? 'bg-red-50 border-red-300' : ''}`}>
        <Checkbox checked={form.is_test} onCheckedChange={(v) => set({ is_test: !!v })} data-testid="inv-is-test" />
        <span><b>TEST – DO NOT FULFILL / DO NOT SHIP</b><br /><span className="text-xs text-neutral-500">Marked in Admin, left out of sales totals, no automatic emails or reminders.</span></span>
      </label>
    </div>
    <div className="space-y-3">
      <div><Label className="text-xs">Notes (shown to the customer)</Label><Textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="inv-notes" /></div>
      <div><Label className="text-xs">Internal notes (staff only)</Label><Textarea rows={2} value={form.internal_notes} onChange={(e) => set({ internal_notes: e.target.value })} data-testid="inv-internal-notes" /></div>
    </div>
  </section>
);

export const InvoiceFormDialog = ({ token, invoice, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (invoice ? formFromInvoice(invoice) : blankForm()));
  const [catalog, setCatalog] = useState({ products: [], services: [], terms: {} });
  const [editServices, setEditServices] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const { calc, error } = useCalc(form, token);
  useEffect(() => { axios.get(`${API}/admin/invoice-catalog`, auth(token)).then((r) => setCatalog(r.data)).catch(() => toast.error('Could not load products/services')); }, [token]);

  const save = async (sendNow) => {
    setSaving(true);
    try {
      const r = invoice
        ? await axios.put(`${API}/admin/invoices/${invoice.id}`, payload(form), auth(token))
        : await axios.post(`${API}/admin/invoices`, { ...payload(form), send_now: sendNow }, auth(token));
      const inv = r.data.invoice;
      if (r.data.email_error) toast.error(`Invoice ${inv.invoice_number} saved as Draft but NOT emailed: ${r.data.email_error}`, { duration: 10000 });
      else toast.success(invoice ? `Invoice ${inv.invoice_number} updated` : `Invoice ${inv.invoice_number} created${sendNow ? ` and emailed to ${inv.customer_email}` : ' as Draft'}`);
      onSaved(inv); onClose();
    } catch (e) { toast.error(err(e, 'Could not save invoice')); } finally { setSaving(false); }
  };
  const canSend = !!form.customer_email && calc && calc.tax_details?.status !== 'pending_address';
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-form-dialog">
        <DialogHeader>
          <DialogTitle>{invoice ? `Edit invoice ${invoice.invoice_number}` : 'Create invoice'}</DialogTitle>
          <DialogDescription>Prices, discounts, tax and totals are calculated on the server. The invoice number is assigned when you save.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <CustomerSection form={form} set={set} token={token} />
          <LineItems form={form} set={set} calc={calc} catalog={catalog} onManageServices={() => setEditServices(true)} />
          <TaxTotalsPanel form={form} set={set} calc={calc} calcError={error} />
          <Footer form={form} set={set} terms={catalog.terms} />
          <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
            {invoice && <Button variant="outline" onClick={() => copyPayLink(invoice.id)} data-testid="inv-copy-paylink">Copy Pay Now link</Button>}
            <Button variant="ghost" onClick={onClose} data-testid="inv-cancel">Cancel</Button>
            <Button variant="outline" disabled={saving || !!error || !calc} onClick={() => save(false)} data-testid="inv-save-draft">{invoice ? 'Save changes' : 'Save as Draft'}</Button>
            {!invoice && <Button disabled={saving || !!error || !canSend} title={canSend ? '' : 'Needs a customer email and a confirmed tax address'} onClick={() => save(true)} data-testid="inv-save-send">Save & email to customer</Button>}
          </div>
        </div>
        {editServices && <ServicesEditor services={catalog.services} token={token} onClose={() => setEditServices(false)} onSaved={(s) => setCatalog({ ...catalog, services: s })} />}
      </DialogContent>
    </Dialog>
  );
};
